import type { AssigneeFacet } from '@/lib/basecamp-assignee'

export type TodosListMode = 'me' | 'org'
export type TodosScope = 'all' | 'hop_only' | 'client_only'
export type TodosStatusFilter = 'all' | 'overdue' | 'due_today' | 'overdue_and_today'

export type TodosViewState = {
  listMode: TodosListMode
  scope: TodosScope
  statusFilter: TodosStatusFilter
  assigneeFacet: AssigneeFacet
  assigneePerson: string | null
  q: string
}

export const DEFAULT_TODOS_VIEW: TodosViewState = {
  listMode: 'me',
  scope: 'all',
  statusFilter: 'all',
  assigneeFacet: 'all',
  assigneePerson: null,
  q: '',
}

const LEGACY_LIST_MODE = 'flownotes-todos-list-mode'
const LEGACY_SCOPE = 'flownotes-todos-scope'
const LEGACY_ASSIGNEE = 'flownotes-todos-assignee-facet'

function readLegacy(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function migrateLegacyTodosView(): TodosViewState {
  const listMode = readLegacy(LEGACY_LIST_MODE) === 'org' ? 'org' : 'me'
  const scopeRaw = readLegacy(LEGACY_SCOPE)
  const scope: TodosScope =
    scopeRaw === 'hop_only' || scopeRaw === 'client_only' || scopeRaw === 'all'
      ? scopeRaw
      : 'all'
  const facetRaw = readLegacy(LEGACY_ASSIGNEE)
  const assigneeFacet: AssigneeFacet =
    facetRaw === 'all' ||
    facetRaw === 'hop' ||
    facetRaw === 'clients' ||
    facetRaw === 'unassigned' ||
    facetRaw === 'person' ||
    facetRaw === 'me'
      ? facetRaw
      : 'all'
  return {
    ...DEFAULT_TODOS_VIEW,
    listMode,
    scope,
    assigneeFacet,
  }
}

export type ActivityViewState = {
  who: 'org' | 'team' | 'mine'
  kind: 'all' | 'comments' | 'completions' | 'updates'
  linkedOnly: boolean
  projectId: string
  q: string
}

export const DEFAULT_ACTIVITY_VIEW: ActivityViewState = {
  who: 'org',
  kind: 'all',
  linkedOnly: false,
  projectId: '',
  q: '',
}

const TODOS_LIST_MODES = new Set<TodosListMode>(['me', 'org'])
const TODOS_STATUS_FILTERS = new Set<TodosStatusFilter>([
  'all',
  'overdue',
  'due_today',
  'overdue_and_today',
])
const TODOS_ASSIGNEE_FACETS = new Set<AssigneeFacet>([
  'all',
  'me',
  'hop',
  'clients',
  'unassigned',
  'person',
])

const STATUS_FILTER_LABELS: Record<TodosStatusFilter, string | null> = {
  all: null,
  overdue: 'Overdue',
  due_today: 'Due today',
  overdue_and_today: 'Overdue + today',
}

const PEOPLE_FILTER_LABELS: Record<AssigneeFacet, string | null> = {
  all: null,
  me: 'Assigned to me',
  hop: 'Hop Studios',
  clients: 'Clients',
  unassigned: 'Unassigned',
  person: null,
}

/** Active chip labels. Me/Org is a lens, not a filter. Hop internal / Client is project names. */
export function describeTodosViewFilters(
  view: Pick<TodosViewState, 'scope' | 'statusFilter' | 'assigneeFacet' | 'assigneePerson'>,
  query = '',
): string[] {
  const labels: string[] = []
  if (view.scope === 'hop_only') labels.push('Hop projects')
  if (view.scope === 'client_only') labels.push('Client projects')
  const status = STATUS_FILTER_LABELS[view.statusFilter]
  if (status) labels.push(status)
  if (view.assigneeFacet === 'person' && view.assigneePerson) {
    labels.push(view.assigneePerson)
  } else {
    const people = PEOPLE_FILTER_LABELS[view.assigneeFacet]
    if (people) labels.push(people)
  }
  const q = query.trim()
  if (q) labels.push(`"${q}"`)
  return labels
}

const TODOS_SCOPES = new Set<TodosScope>(['all', 'hop_only', 'client_only'])

function todosViewPatchFields(
  params: URLSearchParams,
): Partial<TodosViewState> {
  const mode = params.get('mode')
  const status = params.get('status')
  const people = params.get('people')
  const scope = params.get('scope')
  const patch: Partial<TodosViewState> = {}
  if (mode && TODOS_LIST_MODES.has(mode as TodosListMode)) {
    patch.listMode = mode as TodosListMode
  }
  if (status && TODOS_STATUS_FILTERS.has(status as TodosStatusFilter)) {
    patch.statusFilter = status as TodosStatusFilter
  }
  if (people && TODOS_ASSIGNEE_FACETS.has(people as AssigneeFacet)) {
    patch.assigneeFacet = people as AssigneeFacet
  }
  const person = params.get('person')
  if (person) patch.assigneePerson = person
  if (scope && TODOS_SCOPES.has(scope as TodosScope)) {
    patch.scope = scope as TodosScope
  }
  const q = params.get('q')
  if (q) patch.q = q
  return patch
}

function searchParamsHaveTodosKeys(params: URLSearchParams): boolean {
  return Boolean(
    params.get('mode') ||
      params.get('status') ||
      params.get('people') ||
      params.get('scope') ||
      params.get('q') ||
      params.get('person'),
  )
}

/**
 * Apply share-link query (`?mode=org&status=overdue_and_today&people=clients`)
 * from `buildBasecampTodosWebappUrl`. Returns null when the URL has no todos keys.
 */
export function todosViewPatchFromSearchParams(
  params: URLSearchParams,
): Partial<TodosViewState> | null {
  if (!searchParamsHaveTodosKeys(params)) return null
  const patch = todosViewPatchFields(params)
  return Object.keys(patch).length > 0 ? patch : null
}

/** Share-link query replaces last-used view (does not merge leftover chips). */
export function todosViewFromSearchParams(
  params: URLSearchParams,
): TodosViewState | null {
  if (!searchParamsHaveTodosKeys(params)) return null
  return { ...DEFAULT_TODOS_VIEW, ...todosViewPatchFields(params) }
}

const ACTIVITY_WHO = new Set<ActivityViewState['who']>(['org', 'team', 'mine'])
const ACTIVITY_KIND = new Set<ActivityViewState['kind']>([
  'all',
  'comments',
  'completions',
  'updates',
])

function activityViewPatchFields(params: URLSearchParams): Partial<ActivityViewState> {
  const patch: Partial<ActivityViewState> = {}
  const who = params.get('who')
  if (who && ACTIVITY_WHO.has(who as ActivityViewState['who'])) {
    patch.who = who as ActivityViewState['who']
  }
  const kind = params.get('kind')
  if (kind && ACTIVITY_KIND.has(kind as ActivityViewState['kind'])) {
    patch.kind = kind as ActivityViewState['kind']
  }
  if (params.get('linked') === '1') patch.linkedOnly = true
  const project = params.get('project')
  if (project) patch.projectId = project
  return patch
}

function searchParamsHaveActivityKeys(params: URLSearchParams): boolean {
  return Boolean(
    params.get('who') || params.get('kind') || params.get('linked') || params.get('project'),
  )
}

/** Share-link query replaces last-used Activity filters. */
export function activityViewFromSearchParams(
  params: URLSearchParams,
): ActivityViewState | null {
  if (!searchParamsHaveActivityKeys(params)) return null
  return { ...DEFAULT_ACTIVITY_VIEW, ...activityViewPatchFields(params) }
}
